import { FilterFilled } from '@ant-design/icons';
import FilterButton from '@components/shared/buttons/FilterButton';
import * as FormStatusMapper from '@services/mappers/FormStatus';
import { Button, Tag } from 'antd';
import clsx from 'clsx';
import {
	ProfiBazaDateRangeFilter,
	ProfiBazaDictionaryFilter,
	ProfiBazaInputFilter,
} from 'components/shared/paginatedProfiBazaTable/filters/ProfiBazaTableFilter';
import { ProfiBazaColumnProps } from 'components/shared/paginatedProfiBazaTable/GridHelper';
import formatHelpers from 'helper/formatHelpers';
import React from 'react';
import { DictionaryName } from 'services/mappers/DictionaryName';
import { FormStatus } from 'services/src/models';
import { ProfiBazaAPIModels } from 'services/src/profiBazaAPI';

const columns = (
	clearAll: boolean,
	setClearAll: (value: boolean) => void,
	tableFilters:
		| Record<keyof ProfiBazaAPIModels.FormVersionItemDto, string[]>
		| undefined,
	tableSorters:
		| Record<
				keyof ProfiBazaAPIModels.FormVersionItemDto,
				'ascend' | 'descend' | null | undefined
		  >
		| undefined
) => {
	const columns: ProfiBazaColumnProps<
		ProfiBazaAPIModels.FormVersionItemDto
	>[] = [
		{
			dataIndex: 'version',
			key: 'version',
			title: (
				<Button className="btn-filter" aria-label="Sortuj kolumnę">
					Wersja
				</Button>
			),
			sorter: (
				a: ProfiBazaAPIModels.FormVersionItemDto,
				b: ProfiBazaAPIModels.FormVersionItemDto
			): number => a.version?.localeCompare(b.version ?? '') ?? 0,
			sortOrder: tableSorters?.version || null,
			filterDropdown: (filterProps) => (
				<ProfiBazaInputFilter<ProfiBazaAPIModels.FormVersionItemDto>
					columns={columns}
					column="version"
					clearAll={clearAll}
					setClearAll={() => setClearAll(false)}
					filterProps={filterProps}
				/>
			),
			filterIcon: (filtered: boolean) => (
				<FilterButton filtered={filtered} />
			),
			filteredValue: tableFilters?.version || null,
			onFilter: (value, record) => {
				return record.version == value;
			},
		},
		{
			dataIndex: 'createdAt',
			key: 'createdAt',
			title: (
				<Button className="btn-filter" aria-label="Sortuj kolumnę">
					Data utworzenia
				</Button>
			),
			render: (value?: Date) => formatHelpers.formatDate(value),
			width: 150,
			sortOrder: tableSorters?.createdAt || null,
			sorter: (
				a: ProfiBazaAPIModels.FormVersionItemDto,
				b: ProfiBazaAPIModels.FormVersionItemDto
			): number => b.createdAt!.getTime() - a.createdAt!.getTime(),
			filterDropdown: (filterProps) => (
				<ProfiBazaDateRangeFilter<ProfiBazaAPIModels.FormVersionItemDto>
					columns={columns}
					column="createdAt"
					clearAll={clearAll}
					setClearAll={() => setClearAll(false)}
					filterProps={filterProps}
				/>
			),
			filterIcon: (filtered: boolean) => (
				<FilterButton filtered={filtered} />
			),
			filteredValue: tableFilters?.createdAt || null,
			onFilter: (value, record) => {
				return (
					new Date(
						new Date(record.createdAt!).toDateString()
					).getTime() >=
						new Date(
							new Date(
								columns
									.find((x) => x.key == 'createdAt')
									?.filteredValue?.[0]?.toString()!
							).toDateString()
						).getTime() &&
					new Date(
						new Date(record.createdAt!).toDateString()
					).getTime() <=
						new Date(
							new Date(
								columns
									.find((x) => x.key == 'createdAt')
									?.filteredValue?.[1]?.toString()!
							).toDateString()
						).getTime()
				);
			},
		},
		{
			dataIndex: 'validFrom',
			key: 'validFrom',
			title: (
				<Button className="btn-filter" aria-label="Sortuj kolumnę">
					Data od
				</Button>
			),
			sortOrder: tableSorters?.validFrom || null,
			render: (value?: Date) => formatHelpers.formatDate(value),
			sorter: (
				a: ProfiBazaAPIModels.FormVersionItemDto,
				b: ProfiBazaAPIModels.FormVersionItemDto
			): number =>
				(b.validFrom?.getTime() ?? 0) - (a.validFrom?.getTime() ?? 0),
			filterDropdown: (filterProps) => (
				<ProfiBazaDateRangeFilter<ProfiBazaAPIModels.FormVersionItemDto>
					columns={columns}
					column="validFrom"
					clearAll={clearAll}
					setClearAll={() => setClearAll(false)}
					filterProps={filterProps}
				/>
			),
			filterIcon: (filtered: boolean) => (
				<FilterButton filtered={filtered} />
			),
			filteredValue: tableFilters?.validFrom || null,
			onFilter: (value, record) => {
				return (
					new Date(
						new Date(record.validFrom!).toDateString()
					).getTime() >=
						new Date(
							new Date(
								columns
									.find((x) => x.key == 'validFrom')
									?.filteredValue?.[0]?.toString()!
							).toDateString()
						).getTime() &&
					new Date(
						new Date(record.validFrom!).toDateString()
					).getTime() <=
						new Date(
							new Date(
								columns
									.find((x) => x.key == 'validFrom')
									?.filteredValue?.[1]?.toString()!
							).toDateString()
						).getTime()
				);
			},
		},
		{
			dataIndex: 'validTo',
			key: 'validTo',
			title: (
				<Button className="btn-filter" aria-label="Sortuj kolumnę">
					Data do
				</Button>
			),
			sortOrder: tableSorters?.validTo || null,
			render: (value?: Date) => formatHelpers.formatDate(value),
			sorter: (
				a: ProfiBazaAPIModels.FormVersionItemDto,
				b: ProfiBazaAPIModels.FormVersionItemDto
			): number =>
				(b.validTo?.getTime() ?? 0) - (a.validTo?.getTime() ?? 0),
			filterDropdown: (filterProps) => (
				<ProfiBazaDateRangeFilter<ProfiBazaAPIModels.FormVersionItemDto>
					columns={columns}
					column="validTo"
					clearAll={clearAll}
					setClearAll={() => setClearAll(false)}
					filterProps={filterProps}
				/>
			),
			filterIcon: (filtered: boolean) => (
				<FilterButton filtered={filtered} />
			),
			filteredValue: tableFilters?.validTo || null,
			onFilter: (value, record) => {
				return (
					new Date(
						new Date(record.validTo!).toDateString()
					).getTime() >=
						new Date(
							new Date(
								columns
									.find((x) => x.key == 'validTo')
									?.filteredValue?.[0]?.toString()!
							).toDateString()
						).getTime() &&
					new Date(
						new Date(record.validTo!).toDateString()
					).getTime() <=
						new Date(
							new Date(
								columns
									.find((x) => x.key == 'validTo')
									?.filteredValue?.[1]?.toString()!
							).toDateString()
						).getTime()
				);
			},
		},
		{
			dataIndex: 'status',
			key: 'status',
			title: (
				<Button className="btn-filter" aria-label="Sortuj kolumnę">
					Status
				</Button>
			),
			sortOrder: tableSorters?.status || null,
			width: 100,
			render: (value?: FormStatus) => {
				let color =
					value === FormStatus.ACTIVE
						? 'green'
						: value === FormStatus.INACTIVE
						? 'volcano'
						: 'blue';
				const statusValue = FormStatusMapper.map(value!);
				return <Tag color={color}>{statusValue}</Tag>;
			},
			filterDictionary: DictionaryName.FormStatus,
			onFilter: (
				value,
				record: ProfiBazaAPIModels.FormVersionItemDto
			) => {
				return record.status === value;
			},
			filterDropdown: (filterProps) => (
				<ProfiBazaDictionaryFilter<
					ProfiBazaAPIModels.FormVersionItemDto
				>
					columns={columns}
					column="status"
					clearAll={clearAll}
					setClearAll={() => setClearAll(false)}
					filterProps={filterProps}
				/>
			),
			filterIcon: (filtered: boolean) => (
				<FilterButton filtered={filtered} />
			),
			filteredValue: tableFilters?.status || null,
		},
	];
	return columns;
};

export default columns;
